import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/lib/format';
import type { Product, WholesaleTier } from '@/lib/types';

export default function WholesalePrices() {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Product | null>(null);
  const [tiers, setTiers] = useState<WholesaleTier[]>([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [minQty, setMinQty] = useState('');
  const [price, setPrice] = useState('');

  const searchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('products')
        .select('*')
        .eq('active', true)
        .order('name')
        .limit(30);
      const term = search.trim();
      if (term) query = query.ilike('name', `%${term}%`);
      const { data, error: e } = await query;
      if (e) throw e;
      setProducts((data as Product[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(searchProducts, 300);
    return () => clearTimeout(t);
  }, [searchProducts]);

  const toggleWholesale = async (p: Product) => {
    const { error: e } = await supabase
      .from('products')
      .update({ wholesale_enabled: !p.wholesale_enabled, updated_at: new Date().toISOString() })
      .eq('id', p.id);
    if (e) return;
    setProducts((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, wholesale_enabled: !p.wholesale_enabled } : x))
    );
  };

  const openTiers = async (p: Product) => {
    setSelected(p);
    setTiersLoading(true);
    setMinQty('');
    setPrice('');
    const { data } = await supabase
      .from('wholesale_tiers')
      .select('*')
      .eq('product_id', p.id)
      .order('min_quantity', { ascending: true });
    setTiers((data as WholesaleTier[]) ?? []);
    setTiersLoading(false);
  };

  const addTier = async () => {
    if (!selected) return;
    const min = parseInt(minQty, 10);
    const p = parseFloat(price);
    if (Number.isNaN(min) || min < 1 || Number.isNaN(p) || p < 0) return;
    const { data, error: e } = await supabase
      .from('wholesale_tiers')
      .insert({ product_id: selected.id, min_quantity: min, price: p })
      .select('*');
    if (e) return;
    setTiers((prev) =>
      [...prev, ...((data as WholesaleTier[]) ?? [])].sort(
        (a, b) => a.min_quantity - b.min_quantity
      )
    );
    setMinQty('');
    setPrice('');
  };

  const removeTier = async (t: WholesaleTier) => {
    const { error: e } = await supabase.from('wholesale_tiers').delete().eq('id', t.id);
    if (e) return;
    setTiers((prev) => prev.filter((x) => x.id !== t.id));
  };

  return (
    <div>
      <p className="text-sm text-foreground-600 mb-4">
        Activa la venta mayorista por producto y define los precios por cantidad. Cuando un cliente
        mayorista alcanza la cantidad mínima, el precio baja automáticamente.
      </p>

      <div className="relative max-w-md">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar producto..."
          className="w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-8 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-price-tag-3-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">No se encontraron productos.</p>
        </div>
      ) : (
        <div className="mt-5 rounded-lg border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">Precio normal</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Niveles mayoristas</th>
                <th className="px-4 py-3 font-medium">Mayorista</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {products.map((p) => (
                <tr key={p.id} className="hover:bg-background-50">
                  <td className="px-4 py-3 font-medium text-foreground-950">{p.name}</td>
                  <td className="px-4 py-3 text-foreground-700">{formatMoney(p.sale_price, 'RD$')}</td>
                  <td className="px-4 py-3 text-foreground-500 hidden md:table-cell">
                    {p.wholesale_enabled ? 'Configurado' : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleWholesale(p)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition cursor-pointer ${
                        p.wholesale_enabled ? 'bg-primary-500' : 'bg-background-300'
                      }`}
                      title={p.wholesale_enabled ? 'Desactivar' : 'Activar'}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                          p.wholesale_enabled ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => openTiers(p)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                      >
                        <i className="ri-settings-4-line" /> Precios
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={selected != null}
        onClose={() => setSelected(null)}
        title={selected ? `Precios mayoristas — ${selected.name}` : ''}
        footer={
          <button
            onClick={() => setSelected(null)}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            Cerrar
          </button>
        }
      >
        {selected && (
          <div>
            <p className="text-sm text-foreground-600">
              Precio normal: <span className="font-semibold text-foreground-950">{formatMoney(selected.sale_price, 'RD$')}</span>
            </p>

            <div className="mt-4 space-y-2">
              {tiersLoading ? (
                <p className="text-sm text-foreground-500">Cargando...</p>
              ) : tiers.length === 0 ? (
                <p className="text-sm text-foreground-500">Sin niveles configurados.</p>
              ) : (
                tiers.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
                  >
                    <span className="text-sm text-foreground-700">
                      Desde <span className="font-semibold text-foreground-950">{t.min_quantity} uds</span>
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-primary-700">
                        {formatMoney(t.price, 'RD$')}
                      </span>
                      <button
                        onClick={() => removeTier(t)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-700 cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-6-line" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Cantidad mínima
                </label>
                <input
                  type="number"
                  min="1"
                  value={minQty}
                  onChange={(e) => setMinQty(e.target.value)}
                  placeholder="Ej. 6"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Precio mayorista
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Ej. 400"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <button
                onClick={addTier}
                className="inline-flex items-center justify-center rounded-md bg-primary-500 hover:bg-primary-600 px-3 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
              >
                <i className="ri-add-line" />
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}